/**
 * Quitter la page pendant une requête Turbo ne laisse plus de `TypeError: Failed to
 * fetch` en console (QA E1 du 20/10, PR #904). Cas typique : recharger pendant
 * « Chargement du planning… » (frame `project_planning`).
 *
 * Au départ de la page, Chrome coupe les requêtes en cours avec une `TypeError`,
 * pas une `AbortError` : Turbo ne tait que la seconde, émet
 * `turbo:fetch-request-error`, puis passe l'erreur à `requestErrored` (un
 * `console.error` côté frame) si personne ne l'annule.
 *
 * On ne coupe PAS la requête à la source (signal branché sur
 * `turbo:before-fetch-request`) : esbuild avec `--splitting` évalue Turbo dans un
 * chunk partagé AVANT le corps d'`application.js`, et la frame déjà dans le HTML
 * part avant tout écouteur (mesuré, 21/10). On agit donc au moment de l'erreur,
 * où l'écouteur existe forcément : mesuré, elle tombe juste APRÈS `pagehide`.
 * Seule une `TypeError` (panne réseau) survenue page quittée est tue ; avant
 * `pagehide`, ou de retour du cache arrière-avant (`pageshow`), une panne réseau
 * reste signalée.
 *
 * Deux gardes, pas une : annuler `turbo:fetch-request-error` ne coupe que
 * `requestErrored`, et `perform()` relance ensuite l'erreur dans une promesse que
 * la frame n'attend pas (`FrameController#visit`). `unhandledrejection` ne tait que
 * ces erreurs-là, retenues au passage (revues du 21/10) : une `TypeError` quelconque
 * après `pagehide` peut être un bug de code, et le texte d'une panne réseau change
 * d'un navigateur à l'autre.
 */
export function silenceTurboFetchErrorsOnUnload({
  window: win = window,
  document: doc = document,
} = {}) {
  let leaving = false;

  win.addEventListener('pagehide', (event) => {
    if (!event.persisted) leaving = true;
  });
  win.addEventListener('pageshow', () => {
    leaving = false;
  });

  const silencedErrors = new WeakSet();

  doc.addEventListener('turbo:fetch-request-error', (event) => {
    const error = event.detail?.error;
    if (!leaving || !(error instanceof TypeError)) return;

    silencedErrors.add(error);
    event.preventDefault();
  });
  win.addEventListener('unhandledrejection', (event) => {
    if (silencedErrors.has(event.reason)) event.preventDefault();
  });
}
